import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CalendarDays, AlertOctagon, Clock, ChevronRight, Star } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * DeadlineTrackerPanel — Tier 2 counsellor tool.
 *
 * Pulls every application/late deadline attached to any degree on
 * any student's shortlist in this cohort and renders them ordered
 * chronologically with a "days_until" badge + filter chips (all /
 * next 30 / next 60 / late-round). Overdue rows show a rose "past"
 * chip so nothing gets forgotten.
 */
const FILTERS = [
  { key: "all",       label: "All" },
  { key: "next_30",   label: "Next 30 days" },
  { key: "next_60",   label: "30–60 days" },
  { key: "next_90",   label: "60–90 days" },
  { key: "late",      label: "Late round" },
  { key: "past",      label: "Overdue" },
];

export default function DeadlineTrackerPanel({ cohortId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!cohortId) return;
    let cancelled = false;
    api
      .get(`/counsellors/cohorts/${cohortId}/deadlines`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [cohortId]);

  const rows = useMemo(() => {
    const all = data?.deadlines || [];
    switch (filter) {
      case "next_30": return all.filter((r) => r.days_until >= 0 && r.days_until <= 30);
      case "next_60": return all.filter((r) => r.days_until > 30 && r.days_until <= 60);
      case "next_90": return all.filter((r) => r.days_until > 60 && r.days_until <= 90);
      case "late":    return all.filter((r) => r.deadline_type === "late");
      case "past":    return all.filter((r) => r.days_until < 0);
      default:        return all;
    }
  }, [data, filter]);

  if (err) return null;
  if (!data) {
    return (
      <div
        data-testid="deadline-tracker-loading"
        className="mt-6 h-32 rounded-lg border border-stone-200 bg-stone-50/50 animate-pulse"
      />
    );
  }

  if (data.count === 0) {
    return (
      <section
        className="mt-6 border border-dashed border-stone-300 rounded-lg p-6 text-center"
        data-testid="deadline-tracker-empty"
      >
        <CalendarDays className="w-5 h-5 mx-auto text-stone-400" />
        <div className="mt-2 text-sm text-stone-600">
          No deadlines yet — add students and generate their shortlists to
          populate the tracker.
        </div>
      </section>
    );
  }

  return (
    <section
      className="mt-6 border border-stone-200 rounded-lg bg-white p-5"
      data-testid="deadline-tracker"
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#4C1D95]" />
          <span className="label-uppercase text-stone-600">
            Deadline tracker · {data.count} deadlines
          </span>
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="deadline-tracker-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`deadline-filter-${f.key}`}
              aria-pressed={filter === f.key}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                filter === f.key
                  ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                  : "bg-white text-stone-600 border-stone-200 hover:border-[#4C1D95]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="mt-6 text-center text-sm text-stone-500 py-8">
          No deadlines in this window.
        </div>
      ) : (
        <ol className="mt-4 divide-y divide-stone-100" data-testid="deadline-tracker-rows">
          {rows.slice(0, 60).map((r, i) => (
            <li
              key={`${r.student_id}-${r.degree_id}-${r.deadline_type}`}
              className="py-2.5 flex items-center gap-4"
              data-testid={`deadline-row-${i}`}
            >
              <DaysBadge days={r.days_until} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-stone-900 truncate">
                    {r.student_name}
                  </span>
                  {r.starred && (
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" aria-label="Starred" />
                  )}
                  <span className="text-[10px] font-underground-sc tracking-wider text-stone-500 shrink-0">
                    {r.deadline_type === "late" ? "Late round" : "Primary"}
                  </span>
                </div>
                <Link
                  to={`/degree/${r.degree_id}`}
                  className="text-xs text-stone-600 hover:text-[#4C1D95] truncate block"
                >
                  {r.degree_name} · {r.uni_name}
                </Link>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-xs text-stone-700 tabular-nums">
                  {r.deadline}
                </div>
                <div className="text-[10px] text-stone-500">{r.country}</div>
              </div>
              <ChevronRight className="w-3 h-3 text-stone-300 shrink-0" />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function DaysBadge({ days }) {
  if (days < 0) {
    return (
      <span
        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 text-[11px] font-mono"
        data-testid="deadline-days-past"
      >
        <AlertOctagon className="w-3 h-3" /> {Math.abs(days)}d ago
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span
        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200 px-2 py-0.5 text-[11px] font-mono"
        data-testid="deadline-days-soon"
      >
        <Clock className="w-3 h-3" /> in {days}d
      </span>
    );
  }
  return (
    <span
      className="shrink-0 inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-700 border border-stone-200 px-2 py-0.5 text-[11px] font-mono"
      data-testid="deadline-days-later"
    >
      in {days}d
    </span>
  );
}
